import { useTranslation } from 'react-i18next';
import { buildAccountBlockedSupportMailto, CONFIG } from '@hero/core';
import { heroLogoBlue } from '@hero/web-assets';
import { AlertCircleIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';

export type BlockedAccessKind = 'user' | 'org';

export interface BlockedAccessScreenProps {
  kind: BlockedAccessKind;
  /** Used for org-block copy and mailto context. */
  orgName?: string;
  /** E.164 or local phone — included in the English support email body. */
  phone?: string;
  /** Show a logout affordance (default: true on web). */
  showLogout?: boolean;
  onSignOut?: () => void;
}

/**
 * Full-screen gate when a user or org is blocked by Hero staff.
 * Generic copy only — admin `blockMeta` is never shown here.
 * See `apps/backend/src/app/auth/README.md` § Blocking.
 */
export function BlockedAccessScreen({
  kind,
  orgName,
  phone = '',
  showLogout = true,
  onSignOut,
}: BlockedAccessScreenProps) {
  const { t } = useTranslation(['c-main']);

  const copyPrefix = kind === 'user' ? 'account_blocked' : 'org_blocked';
  const mailto = phone
    ? buildAccountBlockedSupportMailto(phone)
    : `mailto:${CONFIG.contact.customerCareEmail}`;

  return (
    <div className="flex min-h-svh w-full flex-1 flex-col items-center justify-between bg-background p-8 text-foreground">
      <div className="flex w-full justify-center pt-4">
        <img
          src={heroLogoBlue}
          alt={t('c-main:app_gate.logo_alt')}
          className="h-10 w-auto"
          decoding="sync"
        />
      </div>

      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center animate-in fade-in duration-300">
        <div className="rounded-full bg-destructive/10 p-6 text-destructive">
          <AlertCircleIcon className="h-12 w-12" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{t(`${copyPrefix}.title`)}</h1>
        <p className="text-base text-muted-foreground">
          {kind === 'org'
            ? t(`${copyPrefix}.description`, { orgName: orgName ?? t(`${copyPrefix}.unknown_org`) })
            : t(`${copyPrefix}.description`)}
        </p>
        <Button asChild className="mt-2">
          <a href={mailto}>{t(`${copyPrefix}.contact_support`)}</a>
        </Button>
        {showLogout && onSignOut && (
          <Button variant="ghost" onClick={onSignOut}>
            {t(`${copyPrefix}.sign_out`)}
          </Button>
        )}
      </div>

      <div className="h-10" aria-hidden />
    </div>
  );
}
